import {ContactRepo} from "../api/contact.repo";
import {inject, Injectable} from "@angular/core";
import {OrgDto} from "../dto/org.dto";
import {SearchFilterRequest, SearchRequest} from "../dto/search/search-table.dto.req";
import {forkJoin, Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {buildLetterAddress, DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {SearchContactRes} from "../dto/search/search-contact.res";
import {arraysEqual, isDefined, nonBlank, safeSplit} from "../../libs/utils/epic.utils";
import {map} from "rxjs/operators";
import {LoggingService} from "../../services/logger/logging.service";
import {ContactDto} from "../dto/contact.dto";
import {Additionals} from "../item/additionals";
import {SearchFilterResponse, SearchFilterValueResponse} from "../dto/search/search-table.dto.res";
import {AddressDto} from "../dto/address.dto";
import {PhoneDto} from "../dto/phone.dto";
import {EmailDto} from "../dto/email.dto";
import {BankAddressDto} from "../dto/bank-address.dto";
import {OccurrenceDto} from "../dto/occurrence.dto";
import {HyperlinkDto} from "../dto/hyperlink.dto";

@Injectable({providedIn: 'root'})
export class ContactDuckDb extends ContactRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override getByIdCached(org: OrgDto, contactId: string): Observable<WrapperDto<ContactDto>> {
    return this.getById(org, contactId);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override getById(org: OrgDto, contactId: string, _add?: Additionals): Observable<WrapperDto<ContactDto>> {
    const selectContact = `
      SELECT c.id                                                           AS id,
             c.type                                                         AS type,
             c.number                                                       AS number,
             COALESCE(c.debtor_no, c.creditor_no)                           AS accountNumber,
             c.name                                                         AS name,
             c.firstname                                                    AS firstname,
             c.salutation                                                   AS salutation,
             c.academic_degree                                              AS academicDegree,
             c.language_code                                                AS languageCode,
             c.debtor_no                                                    AS debtorNumber,
             c.debtor_tax_id                                                AS debtorTaxId,
             c.debtor_sector                                                AS debtorSector,
             c.creditor_no                                                  AS creditorNumber,
             c.creditor_tax_id                                              AS creditorTaxId,
             c.creditor_sector                                              AS creditorSector,
             c.creditor_own_number                                          AS creditorOwnNumber,
             c.notes                                                        AS notes,
             c.external_id                                                  AS externalId,
             c.external_number                                              AS externalNumber,
             c.vehicle_count                                                AS vehicleCount,
             c.id_number                                                    AS idNumber,
             c.insurance_number                                             AS insuranceNumber,
             c.salary                                                       AS salary,
             c.nationality                                                  AS nationality,
             c.editable                                                     AS editable,
             c.contact_id                                                   AS contactId,
             c.company_contact_count                                        AS companyContactCount,
             c.contact_relation_count                                       AS contactRelationCount,
             c.employee_type                                                AS employeeType,
             c.category_names                                               AS categories,
             TRIM(COALESCE(c.firstname, '') || ' ' || COALESCE(c.name, '')) AS fullName
      FROM contact c
      WHERE c.id = ${sqlText(contactId)}`;
    return forkJoin([
      this.duck.sql(selectContact),
      this.readAddresses(org, contactId),
      this.readPhones(org, contactId),
      this.getEmails(org, contactId),
      this.readBankAddresses(org, contactId),
      this.getOccurrences(org, contactId),
      this.getHyperlinks(org, contactId)
    ]).pipe(map(([contactRes, addresses, phones, emails, bankAddresses, occurrences, hyperlinks]) => {
      if (DuckDbHelper.hasRes(contactRes)) {
        contactRes[0].categories = safeSplit(contactRes[0].categories, ',');
        const contact = new ContactDto({
          ...contactRes[0], addresses: addresses.getDatas(), phones: phones.getDatas(), emails: emails.getDatas(),
          bankAddresses: bankAddresses.getDatas(), occurrences: occurrences.getDatas(), hyperlinks: hyperlinks.getDatas()
        });
        return DuckDbHelper.data('ContactDto', contact);
      }
      return DuckDbHelper.data('ContactDto', new ContactDto());
    }));
  }

  // Die Kontaktansicht nimmt diese Listen aus getById und laedt sie nach einer Aenderung einzeln nach. Ohne sie stuenden Anschrift,
  // Telefon und Bankverbindung im Export und fehlten auf dem Bildschirm.
  public override readAddresses(_org: OrgDto, contactId: string): Observable<WrapperDto<AddressDto>> {
    const select = `
      SELECT a.id              AS id,
             a.prime           AS prime,
             a.label           AS label,
             a.street          AS street,
             a.house_number    AS houseNumber,
             a.address_suffix  AS addressSuffix,
             a.city            AS city,
             a.zip_code        AS zipCode,
             a.post_office_box AS postOfficeBox,
             a.country_code    AS countryCode,
             a.distance        AS distance,
             a.external_id     AS externalId
      FROM address a
      WHERE a.contact_id = ${sqlText(contactId)}
      ORDER BY a.prime DESC, a.label`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('AddressDto', (res ?? []).map((r: any) => {
      // Die Ansicht zeigt die Briefform, die sonst core zusammensetzt.
      r.letterAddress = buildLetterAddress({...r, countryAlpha2: r.countryCode});
      return new AddressDto(r);
    }))));
  }

  public override readPhones(_org: OrgDto, contactId: string): Observable<WrapperDto<PhoneDto>> {
    const select = `
      SELECT p.id     AS id,
             p.number AS number,
             p.prime  AS prime,
             p.label  AS label
      FROM phone p
      WHERE p.contact_id = ${sqlText(contactId)}
      ORDER BY p.prime DESC, p.label`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('PhoneDto', (res ?? []).map((r: any) => new PhoneDto(r)))));
  }

  public override getEmails(_org: OrgDto, contactId: string): Observable<WrapperDto<EmailDto>> {
    const select = `
      SELECT e.id         AS id,
             e.email      AS email,
             e.prime      AS prime,
             e.label      AS label,
             e.newsletter AS newsletter
      FROM email e
      WHERE e.contact_id = ${sqlText(contactId)}
      ORDER BY e.prime DESC, e.label`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('EmailDto', (res ?? []).map((r: any) => new EmailDto(r)))));
  }

  public override readBankAddresses(_org: OrgDto, contactId: string): Observable<WrapperDto<BankAddressDto>> {
    const select = `
      SELECT b.id         AS id,
             b.owner_name AS ownerName,
             b.iban       AS iban,
             b.bank_name  AS bankName,
             b.bic        AS bic,
             b.prime      AS prime,
             b.label      AS label
      FROM bank_address b
      WHERE b.contact_id = ${sqlText(contactId)}
      ORDER BY b.prime DESC, b.label`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('BankAddressDto', (res ?? []).map((r: any) => {
      // Die Anzeige erwartet die IBAN in Vierergruppen, wie core sie liefert.
      r.ibanFormated = String(r.iban ?? '').replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
      return new BankAddressDto(r);
    }))));
  }

  public override getOccurrences(_org: OrgDto, contactId: string): Observable<WrapperDto<OccurrenceDto>> {
    const select = `
      SELECT o.id              AS id,
             o.occurrence_date AS "date",
             o.interval        AS "interval",
             o.label           AS label
      FROM occurrence o
      WHERE o.contact_id = ${sqlText(contactId)}
      ORDER BY o.occurrence_date`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('OccurrenceDto', (res ?? []).map((r: any) => new OccurrenceDto(r)))));
  }

  public override getHyperlinks(_org: OrgDto, contactId: string): Observable<WrapperDto<HyperlinkDto>> {
    const select = `
      SELECT h.id        AS id,
             h.reference AS reference,
             h.prime     AS prime,
             h.label     AS label
      FROM hyperlink h
      WHERE h.contact_id = ${sqlText(contactId)}
      ORDER BY h.prime DESC, h.label`;
    return this.duck.sql(select).pipe(map((res: any) => DuckDbHelper.datas('HyperlinkDto', (res ?? []).map((r: any) => new HyperlinkDto(r)))));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchContactRes>> {
    if (arraysEqual(request.fields, ['timeversion'])) {
      return this.typeahead(request);
    }

    const limit: number = request.pageSize;
    const offset: number = request.pageSize * (request.page - 1);
    const sort: string | undefined = this.buildSort(request);
    const query: string | undefined = this.buildQuery(request);
    const filter: string | undefined = this.buildFilter(request);
    const search = `
      SELECT c.id                                                                          AS id,
             c.type                                                                        AS type,
             c.number                                                                      AS number,
             COALESCE(c.debtor_no, c.creditor_no)                                          AS accountNumber,
             TRIM(COALESCE(c.firstname, '') || ' ' || COALESCE(c.name, ''))                AS fullName,
             FALSE                                                                         AS editable,
             c.salutation                                                                  AS salutation,
             c.vehicle_count                                                               AS vehicleCount,
             c.contact_id                                                                  AS contactId,
             c.category_names                                                              AS categories,
             MAX(p.number)                                                                 AS phone,
             TRIM(COALESCE(MAX(a.street), '') || ' ' || COALESCE(MAX(a.house_number), '')) AS streetWithNo,
             MAX(a.city)                                                                   AS city,
             MAX(a.zip_code)                                                               AS zipCode,
             COUNT(*)                                                                         OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM contact c
             LEFT JOIN phone p ON p.contact_id = c.id AND p.belong = 'CONTACT' AND p.prime = TRUE
             LEFT JOIN address a ON a.contact_id = c.id AND a.belong = 'CONTACT' AND a.prime = TRUE
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      GROUP BY c.id, c.type, c.number, COALESCE(c.debtor_no, c.creditor_no), c.salutation, c.vehicle_count,
               c.contact_id, c.category_names, c.firstname,
               c.name
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes: boolean = isDefined(res) && res.length > 0;
      const firstRow: any = hasRes ? res[0] : {total_results: 0} as any;
      if (DuckDbHelper.hasRes(res)) {
        res.filter((r: any) => isDefined(r.categories))
          .forEach((r: any) => r.categories = safeSplit(r.categories, ','));
      }
      const result = new SearchContactRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchContactRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string | undefined {
    let field;
    if (isDefined(request.sortField)) {
      switch (request.sortField) {
        case 'number':
          field = 'c.number';
          break;
        case 'fullName':
          field = 'c.firstname';
          break;
        case 'phone':
          field = 'MAX(p.number)';
          break;
        case 'zipCode':
          field = 'MAX(a.zip_code)';
          break;
        case 'city':
          field = 'MAX(a.city)';
          break;
        case 'accountNumber':
          field = 'COALESCE(c.debtor_no, c.creditor_no)';
          break;
        default:
          this.log.warn('Unhandled sort field "{}"', [request.sortField]);
          field = 'c.number';
      }
    } else {
      field = 'c.number';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(c.number ILIKE ${sqlLike(request.query)}
          OR c.firstname ILIKE ${sqlLike(request.query)}
          OR c.name ILIKE ${sqlLike(request.query)}
          OR p.number ILIKE ${sqlLike(request.query)}
          OR a.city ILIKE ${sqlLike(request.query)}
          OR a.zip_code ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    const filters: string[] = [];
    const categoryFilter: SearchFilterRequest | undefined = request.filters.find((f: any) => f.id === 'category');
    if (isDefined(categoryFilter) && isDefined(categoryFilter.selected) && categoryFilter.selected.length === 1) {
      filters.push(`c.category_names LIKE ${sqlLike(categoryFilter.selected[0])}`);
    }
    // Beide Filter kommen unsichtbar aus der Route und grenzen auf einen Datensatz ein. Bei leerer
    // Auswahl ist "nichts" richtig - "alles" waere die Kontaktliste eines fremden Datensatzes am
    // eigenen Reiter. Siehe sqlText() zum blanken Token NULL.
    const paramContactId: SearchFilterRequest | undefined = request.filters.find((f: any) => f.id === 'param-contact-id');
    if (isDefined(paramContactId) && isDefined(paramContactId.selected)) {
      filters.push(paramContactId.selected.length > 0 ? `c.contact_id  = ${sqlText(paramContactId.selected[0])}` : '1=0');
    }
    const paramContactType: SearchFilterRequest | undefined = request.filters.find((f: any) => f.id === 'param-contact-type');
    if (isDefined(paramContactType) && isDefined(paramContactType.selected)) {
      filters.push(paramContactType.selected.length > 0 ? `c.type  = ${sqlText(paramContactType.selected[0])}` : '1=0');
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }

  private typeahead(request: SearchRequest): Observable<WrapperDto<SearchContactRes>> {
    const filter: SearchFilterRequest = request.filters[0];
    const field: string = filter.filterValueField;
    let dbField: string | undefined;
    switch (field) {
      case 'salutation':
        dbField = 'c.salutation';
        break;
      case 'academicDegree':
        dbField = 'c.academic_degree';
        break;
      default:
        // Ohne Rueckfall stuende woertlich "undefined" als Spaltenname im SQL, und das Filter-Auswahlfeld
        // bliebe mit einem Binder-Fehler leer. contact-list definiert drei Filter, zwei landen hier.
        //
        // ⚠️ Der Rueckfall muss dieselbe FORM haben wie der Erfolgsfall: genau ein SearchContactRes.
        // Ein leerer Wrapper laesst getFirstData() undefined liefern, und die Suchkomponente greift
        // darauf zu - aus dem vermiedenen Binder-Fehler wuerde ein TypeError, also aus einer leeren
        // Auswahlliste eine kaputte Seite.
        this.log.warn('Unhandled typeahead field "{}"', [field]);
        return DuckDbHelper.dataObs('SearchContactRes', this.emptyTypeahead(request, filter));
    }

    const search = `
      SELECT ${dbField} AS name, COUNT(*) AS cnt
      FROM contact c
      WHERE ${dbField} IS NOT NULL
      GROUP BY ${dbField}`;

    return this.duck.sql(search).pipe(map((res) => {
      const hasRes: boolean = isDefined(res) && res.length > 0;
      const filterRes = new SearchFilterResponse(filter);
      filterRes.values = hasRes ? res.map((r: any) => {
        r.count = r.cnt;
        return new SearchFilterValueResponse(r);
      }) : [];
      const result = new SearchContactRes({
        items: [],
        hits: hasRes ? res.length : 0,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: [filterRes]
      });
      return DuckDbHelper.data('SearchContactRes', result);
    }));
  }

  /**
   * Die Form, die der Aufrufer erwartet: ein SearchContactRes ohne Werte, mit zurueckgereichtem Filter.
   *
   * <p>Die Suchkomponente liest {@code filters} und {@code items} vom Ergebnis. Fehlt das Ergebnis ganz,
   * bricht sie ab; ist es leer aber vorhanden, bleibt die Auswahlliste leer — und genau das ist die
   * richtige Auskunft, wenn der Viewer zu diesem Filter nichts anzubieten hat.</p>
   */
  private emptyTypeahead(request: SearchRequest, filter: SearchFilterRequest): SearchContactRes {
    const filterRes: SearchFilterResponse = new SearchFilterResponse(filter);
    filterRes.values = [];
    return new SearchContactRes({
      items: [],
      hits: 0,
      page: request.page,
      pageSize: request.pageSize,
      sortField: request.sortField,
      sortOrder: request.sortOrder,
      filters: [filterRes]
    });
  }
}
